import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@/common/styles/style";
import { useLocation, useNavigate } from "react-router-dom";
import { modifyStyle, styles } from "./style";
import { useState } from "react";
import BackButton from "@/atoms/backButton";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { useDragAndDrop } from "@/hooks/useDragAndDrop";
import { DragDropContext } from "@hello-pangea/dnd";
import Menu from "@/organisms/menu";
import { initialMenuItems } from "./data";
import { apiMachineType, DragDndItemsType } from "@/common/type/type";
import DragArea from "@/organisms/dragArea";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  DataViewType,
  GetDataType,
  GetTrendType,
  TrendViewType,
  WidgetDataType,
  WidgetType,
} from "./type";
import { useAuth } from "@/hooks/useAuth";
import { getRoleName, roleInfo } from "@/helpers/getUserInfo";
import CircularProgress from "@/molecules/circularProgress";
import { Routes } from "@/router/routes";
import ErrorMessage from "@/atoms/errorMessage";

export default function DashboardList() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { machineIds, selectedMachines } = location.state || {};
  const [modify, setModify] = useState<boolean>(false);
  const [filteredMachineId, setFilteredMachineId] = useState<number | null>(
    null
  );
  const [_, setFilteredMachineIndex] = useState<string | null>(null);
  const [widgetQueue, setWidgetQueue] = useState<WidgetType[]>([]);
  const [dataWidgets, setDataWidgets] = useState<DataViewType[]>([]);
  const [trendWidgets, setTrendWidgets] = useState<TrendViewType[]>([]);
  const [totalWidgets, setTotalWidgets] = useState<WidgetDataType[]>([]);
  const {
    loading,
    menuItems,
    dragAreaItems1,
    dragAreaItems2,
    dragAreaItems3,
    dragAreaItems4,
    dragAreaItems5,
    handleDragEnd,
  } = useDragAndDrop({
    initialMenuItems,
    machineIds,
    setDataWidgets,
    setTrendWidgets,
    setWidgetQueue,
    setTotalWidgets,
  });
  const [error, setError] = useState<string | null>(null);
  const { userRole } = useAuth();

  const dataItems: DragDndItemsType[] = [
    dragAreaItems1,
    dragAreaItems2,
    dragAreaItems3,
    dragAreaItems4,
    dragAreaItems5,
  ];

  const handleSetting = (machine: apiMachineType) => {
    navigate(`/MachineList/CollectionDataSet/${machine.id}`, {
      state: { selectedMachine: machine },
    });
  };

  const handleUpdateWidgets = (newWidgets: WidgetDataType[]) => {
    setTotalWidgets((prev) => {
      const updatedWidgets = JSON.parse(JSON.stringify(prev));
      newWidgets.forEach((newWidget) => {
        const existingWidgetIndex = updatedWidgets.findIndex(
          (widget: WidgetDataType) => widget.machineId === newWidget.machineId
        );

        if (existingWidgetIndex >= 0) {
          const existingWidget: WidgetDataType = structuredClone(
            updatedWidgets[existingWidgetIndex]
          );

          newWidget.dataWidgets.forEach((newDataWidget) => {
            const dataList =
              dataItems[machineIds.indexOf(newWidget.machineId)] ?? [];
            const matchingItem = dataList.find(
              (item) => item.position === newDataWidget.position
            );

            if (matchingItem) {
              newDataWidget.sortId = matchingItem.sortId;
            } else if (newDataWidget.sortId === undefined) {
              newDataWidget.sortId = 0;
            }

            if (matchingItem) {
              newDataWidget.sortId = matchingItem.sortId;
            }

            const isDuplicate = existingWidget.dataWidgets.some(
              (existingDataWidget: GetDataType) =>
                existingDataWidget.position === newDataWidget.position
            );

            if (!isDuplicate) {
              existingWidget.dataWidgets = [
                ...existingWidget.dataWidgets,
                structuredClone(newDataWidget),
              ];
            }
          });

          newWidget.trendWidgets.forEach((newTrendWidget) => {
            const dataList =
              dataItems[machineIds.indexOf(newWidget.machineId)] ?? [];
            const matchingItem = dataList.find(
              (item) => item.position === newTrendWidget.position
            );
            if (matchingItem) {
              newTrendWidget.sortId = matchingItem.sortId;
            } else if (newTrendWidget.sortId === undefined) {
              newTrendWidget.sortId = 0;
            }

            const isDuplicate = existingWidget.trendWidgets.some(
              (existingTrendWidget: GetTrendType) =>
                existingTrendWidget.position === newTrendWidget.position
            );

            if (!isDuplicate) {
              existingWidget.trendWidgets = [
                ...existingWidget.trendWidgets,
                structuredClone(newTrendWidget),
              ];
            }
          });

          updatedWidgets[existingWidgetIndex] = structuredClone(existingWidget);
        } else {
          newWidget.dataWidgets.forEach((newDataWidget) => {
            const matchingItem = dataItems[
              machineIds.indexOf(newWidget.machineId)
            ]?.find((item) => item.position === newDataWidget.position);

            if (matchingItem) {
              newDataWidget.sortId = matchingItem.sortId;
            } else if (newDataWidget.sortId === undefined) {
              newDataWidget.sortId = 0;
            }
          });
          newWidget.trendWidgets.forEach((newTrendWidget) => {
            const matchingItem = dataItems[
              machineIds.indexOf(newWidget.machineId)
            ]?.find((item) => item.position === newTrendWidget.position);

            if (matchingItem) {
              newTrendWidget.sortId = matchingItem.sortId;
            } else if (newTrendWidget.sortId === undefined) {
              newTrendWidget.sortId = 0;
            }
          });

          updatedWidgets.push(structuredClone(newWidget));
        }
      });

      return updatedWidgets;
    });
  };

  const handleDashboardModify = (machineId: number, machineNum: string) => {
    setModify(true);
    setFilteredMachineId(machineId);
    setFilteredMachineIndex(machineNum);
  };

  const handleDashboardSave = async () => {
    try {
      const requestBody = totalWidgets.map((widget) => {
        return {
          machineId: widget.machineId,
          dataWidgets: widget.dataWidgets.map(
            ({ sortId, ...dataWidget }) => dataWidget
          ),
          trendWidgets: widget.trendWidgets.map(
            ({ sortId, ...trendWidget }) => trendWidget
          ),
        };
      });

      const response = await fetch("/api/dashboard", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
        credentials: "include",
      });

      const data = await response.json();
      if (response.ok) {
        alert("保存が正常に完了しました。");
        window.location.reload();
        console.log(data.message);
        setModify(false);
        setFilteredMachineId(null);
        setFilteredMachineIndex(null);
        setOpen(false);
      } else {
        console.error("サーバ接続エラー: ", response.statusText);
        if (response.status === 401 || response.status === 403) {
          alert(data.message);
          navigate(Routes.LOGIN);
        } else {
          setError(data.message);
        }
      }
    } catch (error) {
      console.error("サーバ接続エラー:", error);
    }
  };

  const handleCancel = () => {
    const confirmExit = window.confirm(
      "本当に出ますか？\n変更内容は保存されません。"
    );
    if (confirmExit) {
      window.location.reload();
      setModify(false);
      setOpen(false);
    }
  };

  return (
    <Box sx={styles.container}>
      <Box sx={!modify ? styles.subContainer : modifyStyle}>
        <DragDropContext onDragEnd={handleDragEnd}>
          {modify ? (
            <Drawer open={open} variant="persistent" sx={styles.drawer}>
              <Menu menuItems={menuItems} />
            </Drawer>
          ) : null}
          <Box sx={{ marginLeft: open ? "240px" : 0, ...styles.mainArea }}>
            {modify ? (
              <Button
                sx={styles.addWidgetButton}
                onClick={() => setOpen(!open)}
              >
                <AddOutlinedIcon />
                <Typography>ダッシュボードに追加</Typography>
              </Button>
            ) : null}
            <Stack direction="row" spacing={2} sx={styles.titleArea}>
              <BackButton />
              <Typography variant="h2" sx={styles.title}>
                ダッシュボード
              </Typography>
            </Stack>
            <ErrorMessage error={error} />
            {loading ? (
              <CircularProgress />
            ) : widgetQueue.length > 0 || selectedMachines.length > 0 ? (
              selectedMachines
                .filter((machine: apiMachineType) =>
                  filteredMachineId ? machine.id === filteredMachineId : true
                )
                .map((machine: apiMachineType, index: number) => {
                  const widgets = widgetQueue.filter(
                    (widget) => widget.machineId === machine.id
                  );
                  const data = dataWidgets.filter(
                    (widget) => widget.machineId === machine.id
                  );
                  const trend = trendWidgets.filter(
                    (widget) => widget.machineId === machine.id
                  );

                  const machineNumber = selectedMachines.findIndex(
                    (m: apiMachineType) =>
                      m.machineNumber === machine.machineNumber
                  );

                  return (
                    <Card key={`${machine.id}-${index}`} sx={styles.card}>
                      <CardHeader
                        action={
                          <Stack direction="row" spacing={2}>
                            {!modify ? (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() =>
                                  handleDashboardModify(
                                    machine.id,
                                    machineNumber
                                  )
                                }
                              >
                                <Typography variant="button">編集</Typography>
                              </Button>
                            ) : null}
                            {userRole &&
                            getRoleName(userRole) === roleInfo.admin ? (
                              <IconButton
                                onClick={() => handleSetting(machine)}
                              >
                                <SettingsIcon />
                              </IconButton>
                            ) : null}
                          </Stack>
                        }
                        title={`機械: ${machine.machineNumber}`}
                        sx={styles.cardHeaderTitle}
                      />
                      <CardContent>
                        <DragArea
                          dragAreaItems={
                            machineNumber !== -1 ? dataItems[machineNumber] : []
                          }
                          selectedMachine={machine}
                          widgetQueue={widgets}
                          dataWidgets={data}
                          trendWidgets={trend}
                          setDataWidgets={setDataWidgets}
                          setTrendWidgets={setTrendWidgets}
                          onUpdateWidgets={handleUpdateWidgets}
                          setTotalWidgets={setTotalWidgets}
                          modify={modify}
                        />
                      </CardContent>
                    </Card>
                  );
                })
            ) : (
              <p>選択された機械がありません。</p>
            )}
          </Box>
        </DragDropContext>
      </Box>
      {modify ? (
        <Box sx={styles.bottom}>
          <Stack direction="row" spacing={3}>
            <Button variant="outlined" onClick={handleCancel}>
              <Typography variant="button">キャンセル</Typography>
            </Button>
            <Button variant="contained" onClick={handleDashboardSave}>
              <Typography variant="button">保存</Typography>
            </Button>
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
}
